import { Observable } from 'rxjs';

import {
  Container,
  Service,
  IUserSetting,
  IStreakDTO,
  LearningExperienceType,
  ILeaderboard,
} from '../../../symphony';
import { IExperimentsDataResponse } from '../../../api/private/global.interface';
import { ILandingPageApi, ILandingPageResponse } from '../../../api/private/learn-engine-api/landing-page-api.interface';
import {
  ILearnEngineApi, LocationType, ISolveMaterialPayload, ILEHeaders, ISolveMaterialDTO, IMaterial, ILearningPathSubtree, ILessonSubtree, LessonSessionQuitType, IMaterialSolutionDTO, ICourseStructureDTO, ScreenSource, IScreenConfig, ICategorizedCourse,
} from '../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICertificatesApi, CertificatesApi, ICertificate } from '../../../api/public/certificates-api';
import { ICommentsApi, CommentsApi } from '../../../api/public/comments-api';
import { IExperimentApi, ExperimentNames } from '../../../api/public/experiment-api';
import {
  IGamificationApi, GamificationApi, IBuyShopItemPayload, IBuyShopItemResponse,
} from '../../../api/public/gamification-api';
import {
  IKudosApi, KudosApi, IFreeHeartPayload, IKudosResponse, IKudosHeartConfigsResponse,
} from '../../../api/public/kudos-api';
import { LeaderboardApi } from '../../../api/public/leaderboard-api';
import { LandingPageApi } from '../../../api/public/learn-engine/landing-page-api';
import { LearnEngineApi } from '../../../api/public/learn-engine/learn-engine-api';
import { StaticPagesAPI, LanguagesResponseType } from '../../../api/public/static-pages-api';
import { IStreakApi, StreakApi } from '../../../api/public/streak-api';
import { IUserSettingsApi, UserSettingsApi, IUserSettingsResponse } from '../../../api/public/user-settings-api';

@Service()
export class SlApiService {
  private learnEngineApi: ILearnEngineApi;

  private landingPageApi: ILandingPageApi;

  private commentsApi: ICommentsApi;

  private gamificationApi: IGamificationApi;

  private kudosApi: IKudosApi;

  private leaderboardApi: LeaderboardApi;

  private experimentApi: IExperimentApi;

  private userSettingsApi: IUserSettingsApi;

  private streakApi: IStreakApi;

  private staticPagesApi: StaticPagesAPI;

  private certificatesApi: ICertificatesApi;

  constructor() {
    this.learnEngineApi = Container.take('global', LearnEngineApi);
    this.kudosApi = Container.take('global', KudosApi);
    this.landingPageApi = Container.take('global', LandingPageApi);
    this.commentsApi = Container.take('global', CommentsApi);
    this.gamificationApi = Container.take('global', GamificationApi);
    this.leaderboardApi = Container.take('global', LeaderboardApi);
    this.userSettingsApi = Container.take('global', UserSettingsApi);
    this.streakApi = Container.take('global', StreakApi);
    this.staticPagesApi = Container.take('global', StaticPagesAPI);
    this.certificatesApi = Container.take('global', CertificatesApi);
  }

  public enrollCourse(
    alias: string,
    locationTypeId: LocationType,
  ): Observable<null> {
    return this.learnEngineApi.enrollCourse({ alias, locationTypeId });
  }

  public solveMaterial(
    payload: ISolveMaterialPayload,
    session?: string,
    headers?: ILEHeaders,
    isDynamicSession?: boolean,
  ): Observable<ISolveMaterialDTO> {
    return this.learnEngineApi.solveMaterial(payload, session, headers, isDynamicSession);
  }

  public getCourseSubTree(courseAlias: string): Observable<IMaterial> {
    return this.learnEngineApi.getCourseSubtree(courseAlias);
  }

  public getLearningPathSubTree(
    alias: string,
  ): Observable<ILearningPathSubtree> {
    return this.learnEngineApi.getLearningPathSubtree(alias);
  }

  public getCertificate(alias: string): Observable<ICertificate> {
    return this.certificatesApi.getCertificateByCourseAlias(alias);
  }

  public getLessonSubTree(
    materialRelationId: number,
    lessonSessionId?: string,
    headers?: ILEHeaders,
  ): Observable<ILessonSubtree> {
    return this.learnEngineApi.getLessonSubtree(
      materialRelationId,
      lessonSessionId,
      headers,
    );
  }

  public getSkipAheadSubTree(
    materialRelationId: number,
    lessonSessionId?: string,
    headers?: ILEHeaders,
  ): Observable<ILessonSubtree> {
    return this.learnEngineApi.getSkipAheadSubTree(
      materialRelationId,
      lessonSessionId,
      headers,
    );
  }

  public deleteSession(
    materialRelationId: number,
    quitType: LessonSessionQuitType,
  ): Observable<null> {
    return this.learnEngineApi.deleteSession(materialRelationId, quitType);
  }

  public getMaterialSolution(
    materialRelationId: number,
    sessionId?: string,
  ): Observable<IMaterialSolutionDTO> {
    return this.learnEngineApi.getMaterialSolution(
      materialRelationId,
      sessionId,
    );
  }

  public getLPCommentsCount(commentContainerId: number): Observable<number> {
    return this.commentsApi.getLPCommentsCount(commentContainerId);
  }

  public unlockItemWithBits(
    payload: IBuyShopItemPayload,
  ): Observable<IBuyShopItemResponse> {
    return this.gamificationApi.buyShopItem(payload);
  }

  public getLandingPage(alias: string): Observable<ILandingPageResponse> {
    return this.landingPageApi.getLandingPage(alias);
  }

  public getCourseStructure(
    alias: string,
    type: LearningExperienceType,
  ): Observable<ICourseStructureDTO> {
    return this.learnEngineApi.getCourseStructure(alias, type);
  }

  public refillFreeHearts(
    data: IFreeHeartPayload,
  ): Observable<IKudosResponse> {
    return this.kudosApi.freeHeartRefill(data);
  }

  public getHeartConfigs(): Observable<IKudosHeartConfigsResponse> {
    return this.kudosApi.getHeartConfigs();
  }

  public getLeaderboardPredictedState(xp: number): Observable<ILeaderboard> {
    return this.leaderboardApi.getLeaderboardPredictedState(xp);
  }

  public getScreensConfig(source: ScreenSource): Observable<IScreenConfig[]> {
    return this.learnEngineApi.getScreensConfig(source);
  }

  public getExperimentsData(
    userId: number,
    experimentsArray: ExperimentNames[],
  ): Observable<IExperimentsDataResponse> {
    return this.experimentApi.getExperimentsData(userId, experimentsArray);
  }

  public getCourseCategories(): Observable<ICategorizedCourse[]> {
    return this.learnEngineApi.getCategories();
  }

  public getUserSettings(): Observable<IUserSettingsResponse> {
    return this.userSettingsApi.getUserSettings();
  }

  public updateUserSettings(
    data: IUserSetting[],
  ): Observable<IUserSettingsResponse> {
    return this.userSettingsApi.updateUserSettings(data);
  }

  public setStreakGoal(userId: number, goalDays: number): Observable<null> {
    return this.streakApi.setStreakGoal(userId, goalDays);
  }

  public getPredictedStreaks(userId: number): Observable<IStreakDTO> {
    return this.streakApi.getPredictedStreaks(userId);
  }

  public getLanguages(): Observable<LanguagesResponseType> {
    return this.staticPagesApi.getLanguages();
  }

  public updateSelectedLocale(
    locale: string,
    alias: string,
  ): Observable<null> {
    return this.learnEngineApi.updateSelectedLocale(alias, locale);
  }
}
